import axios from 'axios';
import { env } from '@/config/env';
import { tokenStorage } from '@vnetwork/margarita-core/auth';
import { generateCodeChallenge, generateCodeVerifier } from '@vnetwork/margarita-core/auth';
import { decodeJwtPayload } from '@vnetwork/margarita-core/auth';

export const SSO_VERIFIER_KEY = 'pkce_code_verifier';
export const SSO_INTENDED_KEY = 'intended_url';

/** The OAuth2 redirect URI handled by the callback route. */
export function ssoRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Begin the SSO (VNETWORK-ID) login: generate PKCE, persist the verifier, and
 * redirect to the Keycloak authorization endpoint. The `code` comes back to
 * `/auth/callback`, which exchanges it for app tokens via the backend.
 *
 * `idpHint` maps to Keycloak's `kc_idp_hint` — pass a configured identity
 * provider alias (e.g. 'google') to skip the Keycloak login form and go straight
 * to that social provider. The rest of the flow (callback + code exchange) is
 * identical, so a social login needs no backend change beyond the Keycloak IdP
 * being configured.
 */
export async function startSsoLogin(intendedUrl = '/', idpHint?: string): Promise<void> {
  tokenStorage.clear();
  sessionStorage.setItem(SSO_INTENDED_KEY, intendedUrl);

  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  sessionStorage.setItem(SSO_VERIFIER_KEY, verifier);

  const authorizeUrl = `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    client_id: env.keycloak.clientId,
    redirect_uri: ssoRedirectUri(),
    response_type: 'code',
    scope: 'openid profile email',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt: 'login',
    max_age: '0',
  });
  if (idpHint) params.set('kc_idp_hint', idpHint);

  window.location.href = `${authorizeUrl}?${params.toString()}`;
}

/**
 * Best-effort server-side Keycloak logout. Clearing local tokens alone leaves
 * the SSO cookie on the Keycloak host alive, so the next login (even with
 * `prompt=login`) re-authenticates the OLD user with no email field — a
 * customer with two accounts can never switch. Killing the session via the
 * logout endpoint (client_id + refresh_token works for public clients, no
 * id_token needed, no confirmation page) frees the next login to pick any
 * account. `keepalive` lets the request survive the immediate navigation to
 * /auth/login; failures are ignored — a dead session (e.g. forced logout after
 * a failed refresh) is already the desired end state.
 */
export function endKeycloakSession(refreshToken: string): void {
  const logoutUrl = `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/logout`;
  void fetch(logoutUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: env.keycloak.clientId,
      refresh_token: refreshToken,
    }).toString(),
    keepalive: true,
  }).catch(() => {});
}

/** Decode the (unverified) JWT payload for exp/sub — session bootstrap only. */
export function jwtClaims(token: string): { exp?: number; sub?: string } {
  return decodeJwtPayload(token) as { exp?: number; sub?: string };
}

/**
 * Shape the session input from an SSO token bundle — used by both the direct
 * callback exchange and the post-MFA verify, so they stay in sync. `exp` falls
 * back to the JWT's own claim when the response omits it (the MFA-verify
 * response carries `expires_in`, not an absolute `exp`).
 */
export function ssoSessionFromTokens(t: {
  access_token: string;
  refresh_token?: string;
  exp?: number;
}) {
  const claims = jwtClaims(t.access_token);
  return {
    access_token: t.access_token,
    refresh_token: t.refresh_token,
    exp: t.exp ?? claims.exp,
    uuid: claims.sub,
  };
}

/**
 * Keycloak Direct Access Grant (password grant) — authenticate WITHOUT a
 * redirect, for builds where the SSO redirect is disabled (brand.features.sso =
 * false). Posts username/password straight to the realm token endpoint and
 * shapes the result like the SSO callback, so the session still refreshes via
 * Keycloak (authMethod 'sso'). Requires the client to be public with Direct
 * Access Grants enabled; bad credentials surface as a 401/400 for the caller.
 */
export async function directGrantLogin(email: string, password: string) {
  tokenStorage.clear();
  const tokenUrl = `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/token`;
  const res = await axios.post(
    tokenUrl,
    new URLSearchParams({
      grant_type: 'password',
      client_id: env.keycloak.clientId,
      username: email,
      password,
      scope: 'openid profile email',
    }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  // `exp` omitted → ssoSessionFromTokens derives the absolute expiry from the
  // access token's own `exp` claim.
  return ssoSessionFromTokens({
    access_token: res.data?.access_token,
    refresh_token: res.data?.refresh_token,
  });
}
